import {
  Box,
  TextField as MuiTextField,
  type TextFieldProps as MuiTextFieldProps,
} from '@mui/material';
import {visuallyHidden} from '@mui/utils';
import {WarningCircle} from '@phosphor-icons/react';
import React from 'react';

export type TextFieldProps = Omit<
  MuiTextFieldProps,
  'variant' | 'label' | 'id' | 'slotProps'
> & {
  /** Required: the visible label above the box is the field's name. */
  label: string;
  /** Required: the <label for> and the helper's aria-describedby hang on it. */
  id: string;
  /**
   * Keep the label (still the field's name, via <label for>) but hide it
   * visually: only where the field's purpose is already shown, e.g. a search
   * box with a search icon and a placeholder.
   */
  hideLabel?: boolean;
  // `object`, not Record<string, unknown>: an Autocomplete's `renderInput`
  // params (InputHTMLAttributes, no index signature) must pass through.
  slotProps?: {
    htmlInput?: object;
    input?: object;
    /** With `select`: MUI Select props such as `renderValue`, `MenuProps`. */
    select?: object;
  };
};

/** Taller box under a finger or on a phone, as ui/DataTable rows. */
const TOUCH = '@media (pointer: coarse), (max-width: 599.95px)';

/**
 * The text field of the primitive sheet: the label ABOVE the box rather than
 * floating inside it (no notch cut in the outline), 34px tall (40 on touch),
 * the helper wired as the input's description, and an error that is never
 * colour alone — a 2px danger boundary, aria-invalid, and an icon beside the
 * message. The label stays text.primary in every state; the message carries
 * the error.
 */
export const TextField = React.forwardRef<HTMLDivElement, TextFieldProps>(
  (
    {label, id, hideLabel = false, error, helperText, slotProps, sx, ...rest},
    ref,
  ) => {
    const helper =
      error && helperText ? (
        <Box
          component="span"
          sx={{display: 'inline-flex', alignItems: 'flex-start', gap: 0.5}}>
          <WarningCircle
            aria-hidden
            data-testid="text-field-error-icon"
            size={14}
            style={{flexShrink: 0, marginTop: 1}}
          />
          <span>{helperText}</span>
        </Box>
      ) : (
        helperText
      );
    return (
      <MuiTextField
        ref={ref}
        {...rest}
        id={id}
        variant="outlined"
        label={label}
        error={error}
        helperText={helper}
        slotProps={{
          ...slotProps,
          inputLabel: {shrink: true},
          // No `label` on the outline: it is what cuts the notch (and draws a
          // hidden <legend>) for a floating label this field never has.
          input: {...slotProps?.input, notched: false, label: null},
        }}
        sx={[
          (theme) => ({
            '& .MuiInputLabel-root': {
              position: 'static',
              transform: 'none',
              maxWidth: 'none',
              whiteSpace: 'normal',
              mb: '5px',
              fontSize: theme.typography.caption.fontSize,
              lineHeight: theme.typography.caption.lineHeight,
              // The T2 `title` weight (600); a bare test theme has no `title`.
              fontWeight:
                theme.typography.title?.fontWeight ??
                theme.typography.fontWeightMedium,
              color: 'text.primary',
              '&.Mui-focused, &.Mui-error': {color: 'text.primary'},
              ...(hideLabel && visuallyHidden),
            },
            '& .MuiOutlinedInput-input': {
              py: '7px',
              px: '10px',
              height: 20,
              [TOUCH]: {py: '10px'},
            },
            '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline':
              {borderWidth: 2},
            '& .MuiFormHelperText-root': {
              mx: 0,
              mt: 0.5,
              fontSize: theme.typography.caption.fontSize,
              lineHeight: theme.typography.caption.lineHeight,
            },
          }),
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      />
    );
  },
);
TextField.displayName = 'TextField';
